import type { FieldSet, RecordData } from '@rogoag/airtable/crm';
import { LocalAtRecord } from '../../../db';
import { is_attachment } from '../../../db_ops/airtable_record_ops';
import { fileToBase64 } from '../../../utilities/file-utils';
import { uuidv4 } from '../../../utils';
import logger from '../../../logger';
import S3 from '../../../aws';

export class BoxesDataUploaderService {
  private bucket: string;

  constructor() {
    this.bucket = import.meta.env.PROD ? 'boxes-shipping' : 'boxes-shipping-dev';
  }

  /**
   * Upload updates to aws boxes-shipping bucket for subsequent
   * processing by boxes-shipping-processor lambda.
   */
  async upload(boxRecordsToUpdate: LocalAtRecord[], boxShipmentRecordID: string) {
    await logger.log('SUBMIT_BOXES - transferUpdates start');

    const updates = await this.prepareUpdatesForTransfer(boxRecordsToUpdate);
    const filename = `box_shipment_record_${boxShipmentRecordID}_uuid_${uuidv4()}_boxes_update.json`;

    await logger.log('SUBMIT_BOXES - transferUpdates', { filename, bucket: this.bucket });

    await S3.upload(filename, new Blob([JSON.stringify(updates)], { type: 'plain/text' }), 'application/json', {
      bucket: this.bucket,
    });

    await logger.log('SUBMIT_BOXES - transferUpdates end');
  }

  private async prepareUpdatesForTransfer(records: LocalAtRecord[]) {
    // filter out duplicate records by record ID
    records = records.filter((record, index) => records.indexOf(record) === index);

    const prepareAttachmentsAndFillFields = async (record: LocalAtRecord) => {
      const fields = {};

      const attachments = record.getAttachments();

      for (const attachment of attachments) {
        if (!attachment.dirty) {
          continue;
        }

        if (!attachment.content) {
          attachment.dispose();

          continue;
        }

        // update the attachment fields for the update request
        if (!fields[attachment.field_name]) {
          fields[attachment.field_name] = [];
        }

        const imageInBase64 = await fileToBase64(attachment.content);

        (fields[attachment.field_name] as { fileName: string; fileContent: string }[]).push({
          fileName: attachment.filename,
          fileContent: imageInBase64,
        });

        // must dispose the attachment so it can get updated with new attachment ID
        attachment.dispose();
      }

      // fill in fields
      for (const modField of record.modified_fields) {
        const fieldValue = record.fields[modField];

        if (!is_attachment(fieldValue)) {
          // for attachment fields, don't override fields
          fields[modField] = fieldValue;
        }
      }

      // assemble data update payload
      const update: [table: string, recordUpdate: RecordData<FieldSet>] = [
        record.table,
        {
          id: record.id,
          fields: fields,
        },
      ];

      return update;
    };

    return await Promise.all(records.map(prepareAttachmentsAndFillFields));
  }
}
